import { ApplicationTypes, type CircularHole, Model, rotateVectorOntoBentArm } from 'metalworks-domain'
import { attachComponent, maxMachinePlateSize } from 'metalworks-domain'

import { ArrowHeadType, dimensionIndicatorComponent } from '@salsita-ai/platform-project-sdk/domain'

const maxSize = Math.max(maxMachinePlateSize.length, maxMachinePlateSize.width)

export const scaleIndicator = (min: number, max: number, plateSize: number) => {
  return Math.min(max, Math.max(min, max * (plateSize / maxSize)))
}

interface BendAwareHole {
  $absCoords: { x: number; y: number }
  $plate: {
    $activeBend: {
      direction: Model.BendDirection
      orientation: Model.BendOrientation
      $middlePoint: { x: number; y: number }
    } | null
  }
}

interface DimensionIndicatorCoordinates {
  startX?: number
  startY?: number
  startZ?: number
  endX?: number
  endY?: number
  endZ?: number
  extrusionX?: number
  extrusionY?: number
  extrusionZ?: number
}

/** Rotate hole-local dimension indicator coordinates onto the bent plate arm. */
export const bendHoleDimensionIndicatorCoordinates = (
  hole: BendAwareHole,
  coordinates: DimensionIndicatorCoordinates
) => {
  const normalizedCoordinates = {
    startX: coordinates.startX ?? 0,
    startY: coordinates.startY ?? 0,
    startZ: coordinates.startZ ?? 0,
    endX: coordinates.endX ?? 0,
    endY: coordinates.endY ?? 0,
    endZ: coordinates.endZ ?? 0,
    extrusionX: coordinates.extrusionX ?? 0,
    extrusionY: coordinates.extrusionY ?? 0,
    extrusionZ: coordinates.extrusionZ ?? 0,
  }
  const bend = hole.$plate.$activeBend
  const isOnBentArm =
    bend != null &&
    ((bend.orientation === Model.BendOrientation.Horizontal && hole.$absCoords.y >= bend.$middlePoint.y) ||
      (bend.orientation === Model.BendOrientation.Vertical && hole.$absCoords.x >= bend.$middlePoint.x))

  if (!bend || !isOnBentArm) {
    return normalizedCoordinates
  }

  const transform = (x = 0, y = 0, z = 0) => rotateVectorOntoBentArm({ x, y, z }, bend.orientation, bend.direction)

  const start = transform(normalizedCoordinates.startX, normalizedCoordinates.startY, normalizedCoordinates.startZ)
  const end = transform(normalizedCoordinates.endX, normalizedCoordinates.endY, normalizedCoordinates.endZ)
  const extrusion = transform(
    normalizedCoordinates.extrusionX,
    normalizedCoordinates.extrusionY,
    normalizedCoordinates.extrusionZ
  )

  return {
    startX: start.x,
    startY: start.y,
    startZ: start.z,
    endX: end.x,
    endY: end.y,
    endZ: end.z,
    extrusionX: extrusion.x,
    extrusionY: extrusion.y,
    extrusionZ: extrusion.z,
  }
}

export const createInnerRadiusDimensionIndicatorComponent = (entityType: CircularHole['type']) => {
  return (
    attachComponent
      .withEntity(entityType)
      .withComponent(dimensionIndicatorComponent, 'radius')
      .withFilter({
        navigationStates: [ApplicationTypes.NavigationState.Finalization],
      })
      // TODO: memoEval
      .attach(s => {
        return {
          ...bendHoleDimensionIndicatorCoordinates(s, {
            startX: 0,
            endX: 0,
            startY: -s.radius,
            endY: s.radius,
            startZ: s.$plate.thickness + 0.1,
            endZ: s.$plate.thickness + 0.1,
          }),
          arrowHeadType: ArrowHeadType.CONE,
          arrowHeadSize: 4,
          labelFontSize: scaleIndicator(4, 64, Math.max(s.$plate.$sizeX, s.$plate.$sizeY)),
          label: `R=${s.radius}`,
        }
      })
  )
}
